/* eslint no-underscore-dangle: ["error", { "allow": ["_id", "_created", "_items", "_links"] }] */
import _ from 'lodash';
import queryString from 'query-string';
import moment from 'moment-timezone';

import crud from './crud';
import settings from '../../config/settings';
import statusOptions from '../../config/wo-status-options.json';

import {
  WORK_ORDER_STATUS_UPDATE,
  WORK_ORDER_CREATED,
  WORK_ORDER_ADD_COMMENT,
  WORK_ORDER_ADD_ATTACHMENT,
  SET_FILTER,
  API_CLEAR,
} from '../types';

export const getWorkordersParams = function getWorkordersParams(userData, selectedAffiliateInfo, channelSubscriptions, filterKey, sortKey) {
  let whereParam = '';
  let filterParam = {};

  const affiliateId = selectedAffiliateInfo.id.replace(/-/g, '');
  const priorityOptions = {
    normal: 0,
    high: 1,
    immediate: 2,
  };

  switch (filterKey) {
    case 'assigned':
      filterParam = {
        'assigned.id': userData.id,
      };
      break;

    case 'all': {
      let closedCanceledOptions = [];
      closedCanceledOptions = closedCanceledOptions.concat(statusOptions.closed);
      closedCanceledOptions = closedCanceledOptions.concat(statusOptions.canceled);
      filterParam = {
        $or: [{
          status: {
            $in: statusOptions.open,
          },
        }, {
          status: {
            $in: closedCanceledOptions,
          },
          _updated: {
            $gte: moment.tz(selectedAffiliateInfo.time_zone).subtract(1, 'days').utc().format('ddd, DD MMM YYYY HH:mm:ss [GMT]'),
          },
        }],
      };
      break;
    }

    case 'late':
      filterParam = {
        status: {
          $in: statusOptions.open,
        },
        due_date: {
          $lt: moment.tz(selectedAffiliateInfo.time_zone).utc().format('ddd, DD MMM YYYY HH:mm:ss [GMT]'),
        },
      };
      break;

    case 'normal':
    case 'high':
    case 'immediate':
      filterParam = {
        status: {
          $in: statusOptions.open,
        },
        priority: priorityOptions[filterKey],
      };
      break;

    default:
      filterParam = {
        status: {
          $in: statusOptions[filterKey],
        },
      };
      break;
  }

  // set the channels or created_by.id

  let channelCreatedOr = {
    $or: [{
      channel: {
        $in: channelSubscriptions,
      },
    }, {
      'created_by.id': userData.id,
    }],
  };

  if (_.has(filterParam, '$or')) {
    const existingOr = Object.assign([], filterParam.$or);
    delete filterParam.$or;
    const newChannelCreatedOr = {
      $and: [{
        $or: existingOr,
      },
        channelCreatedOr,
      ],
    };
    channelCreatedOr = newChannelCreatedOr;
  }

  if (userData.attributes.is_superuser && !!userData.attributes.is_superuser) {
    whereParam = Object.assign({}, filterParam);
  } else {
    whereParam = Object.assign({}, filterParam, channelCreatedOr);
  }

  whereParam.affiliate_id = affiliateId;

  return `sort=${sortKey}&where=${JSON.stringify(whereParam)}`;
};

export const retrieveWorkorders = function retrieveWorkorders(page, noLoader) {
  return function retrieveWorkordersFunction(dispatch, getState) {
    const {
      session: {
        affiliate: selectedAffiliateInfo,
        user,
        channels,
      },
      filter: {
        workordersList: {
          filterKey,
          sortKey,
        },
      },
    } = getState();

    const woChannels = channels
      .filter(ch => ch.type === 'work_order')
      .map(ch => ch.name);

    const params = getWorkordersParams(user, selectedAffiliateInfo, woChannels, filterKey, sortKey);
    dispatch(crud.apiRead({
      type: 'workordersList',
      urlParams: {
        page,
        params,
      },
      noLoader,
    }))
    .catch((err) => {
      console.error(err);
    });
  };
};

export const retrieveMoreWorkorders = function retrieveMoreWorkorders() {
  return function retrieveMoreWorkordersFunction(dispatch, getState) {
    const {
      api: {
        nextAction: {
          workordersList,
        },
      },
    } = getState();

    if (workordersList) {
      dispatch(retrieveWorkorders(workordersList));
    }
  };
};

export const retrieveWorkorderConfigs = function retrieveWorkorderConfigs(page, noLoader) {
  return function retrieveWorkorderConfigsFunction(dispatch, getState) {
    const state = getState();
    const { session: { affiliate: selectedAffiliateInfo } } = state;

    if (selectedAffiliateInfo) {
      dispatch(crud.apiRead({
        type: 'workorderConfigs',
        urlParams: {
          affiliateId: selectedAffiliateInfo.id,
          page,
        },
        noLoader,
      }))
      .then((results) => {
        if (results._links.next && results._links.next.href !== null && settings.enable_all_data_fetch === true) {
          const params = queryString.parse(results._links.next.href.split('?')[1]);
          dispatch(retrieveWorkorderConfigs(params.page || null, noLoader));
        }
      })
      .catch((err) => {
        console.error(err);
      });
    }
  };
};

export const retrieveWorkorderById = function retrieveWorkorderById(workorderId) {
  return function retrieveWorkorderByIdFunction(dispatch) {
    dispatch(crud.apiRead({
      type: 'workorder',
      urlParams: {
        workorderId,
      },
    }))
    .catch((err) => {
      console.error(err);
    });
  };
};

export const retrieveWorkOrderCommentById = function retrieveWorkOrderCommentById(commentId) {
  return function retrieveWorkOrderCommentByIdFunction(dispatch) {
    dispatch(crud.apiRead({
      type: 'workorderComment',
      urlParams: {
        commentId,
      },
    }))
    .catch((err) => {
      console.error(err);
    });
  };
};

export const retrieveWorkorderComments = function retrieveWorkorderComments(workorderId, page) {
  return function retrieveWorkorderCommentsFunction(dispatch) {
    dispatch(crud.apiRead({
      type: 'workorderComments',
      urlParams: {
        workorderId,
        page,
      },
    }))
    .then((results) => {
      if (results._links.next && results._links.next.href !== null && settings.enable_all_data_fetch === true) {
        const params = queryString.parse(results._links.next.href.split('?')[1]);
        dispatch(retrieveWorkorderComments(params.page || null));
      }
    })
    .catch((err) => {
      console.error(err);
    });
  };
};

export const createWorkorderCommentAttachment = function createWorkorderCommentAttachment(workorderId, commentId, attachment) {
  return function createWorkorderCommentAttachmentFunction(dispatch) {
    dispatch(crud.apiUpload({
      type: 'attachments',
      urlParams: {
        commentId,
      },
    }, 'attachment', attachment))
    .then(() => {
      dispatch(retrieveWorkOrderCommentById(commentId));
      dispatch({
        type: WORK_ORDER_ADD_ATTACHMENT,
        payload: {
          id: workorderId,
        },
      });
    })
    .catch((err) => {
      console.error(err);
    });
  };
};

export const createWorkorderComment = function createWorkorderComment(workorderId, commentData) {
  return function createWorkorderCommentFunction(dispatch, getState) {
    const {
      session: {
        user,
        affiliate: selectedAffiliateInfo,
      },
    } = getState();
    const data = Object.assign({}, commentData);
    let hasAttachment = false;
    if (_.has(data, 'attachment')) {
      hasAttachment = true;
      delete data.attachment;
    }
    const config = {
      type: 'comments',
      data: Object.assign({}, data, {
        acting_user_full_name: `${user.attributes.first_name} ${user.attributes.last_name}`,
        acting_user: user.id.replace(/-/g, ''),
        work_order_id: workorderId,
        affiliate_id: selectedAffiliateInfo.id.replace(/-/g, ''),
        created_by: {
          id: user.id,
          full_name: `${user.attributes.first_name} ${user.attributes.last_name}`,
        },
      }),
    };
    dispatch(crud.apiCreate(config))
      .then((result) => {
        if (hasAttachment === true) {
          dispatch(createWorkorderCommentAttachment(workorderId, result._id, commentData.attachment));
        }
        dispatch({
          type: WORK_ORDER_ADD_COMMENT,
          payload: {
            id: workorderId,
          },
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };
};

export const createWorkorder = function createWorkorder(workorderData) {
  return function createWorkorderFunction(dispatch, getState) {
    const {
      session: {
        user,
        affiliate: selectedAffiliateInfo,
      },
    } = getState();
    const data = Object.assign({}, workorderData);
    let hasComments = false;
    if (_.has(data, 'comments')) {
      hasComments = true;
      delete data.comments;
    }
    const config = {
      type: 'workorders',
      data: Object.assign({}, data, {
        acting_user_full_name: `${user.attributes.first_name} ${user.attributes.last_name}`,
        acting_user: user.id.replace(/-/g, ''),
        affiliate_id: selectedAffiliateInfo.id.replace(/-/g, ''),
        created_by: {
          id: user.id,
          full_name: `${user.attributes.first_name} ${user.attributes.last_name}`,
        },
      }),
    };
    dispatch(crud.apiCreate(config))
      .then((results) => {
        if (hasComments === true) {
          workorderData.comments.forEach((commentData) => {
            dispatch(createWorkorderComment(results._id, commentData));
          });
        }
        dispatch({
          type: WORK_ORDER_CREATED,
          payload: {
            id: results._id,
          },
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };
};

export const updateWorkorder = function updateWorkorder(workorderId, payload) {
  return function updateWorkorderFunction(dispatch, getState) {
    const {
      session: {
        user,
      },
    } = getState();
    if (payload.status) {
      dispatch({
        type: WORK_ORDER_STATUS_UPDATE,
        payload: {
          id: workorderId,
          status: payload.status,
        },
      });
    }
    const data = Object.assign({}, payload, {
      acting_user_full_name: `${user.attributes.first_name} ${user.attributes.last_name}`,
      acting_user: user.id.replace(/-/g, ''),
    });
    dispatch(crud.apiUpdate({
      type: 'workorder',
      urlParams: {
        workorderId,
      },
      data,
    }))
    .then(() => {
      // return results;
    })
    .catch((err) => {
      console.error(err);
    });
  };
};

export const clearWorkorders = function clearWorkorders() {
  return {
    type: API_CLEAR,
    payload: {
      resource: {
        type: 'work_order',
      },
    },
  };
};

export const filterWorkorder = function filterWorkorder(filterKey, sortKey) {
  return function filterWorkorderFunction(dispatch) {
    dispatch({
      type: SET_FILTER,
      payload: {
        workordersList: {
          filterKey,
          sortKey,
        },
      },
    });
    dispatch(clearWorkorders());
    dispatch(retrieveWorkorders());
  };
};



// WEBPACK FOOTER //
// ./src/state/actions/workorders.js